import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ClipboardList, SearchX } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle, Callout, Checkbox,
  EmptyState, FilePreview, FileUpload, FormActionBar, FormField, Input, NativeSelect, PageHeader, ProgressBar,
  ResetButton, Textarea,
} from "../../ui";
import Shimmer from "../../Common/Shimmer";
import apiClient from "../../../utils/apiClient";
import { getCurrentUser } from "../../../utils/auth";
import { fromDateTimeLocal, toDateTimeLocal } from "../../../utils/datetime";
import { ACCEPT, ACCEPTED_HINT, MAX_FILE_MB, MAX_FILES, fileProblem } from "./assignmentFiles";
import {
  LIMITS, assignmentsBase, dueInDays, formatDue, parseNumber, relativeTime, validateAssignmentForm,
} from "./assignmentRules";
import useUploadRequest, { uploadFailure } from "./useUploadRequest";

const BATCH_PAGE = 200;

const idOf = (v) => String(v?._id || v || "");

const BLANK = { batchId: "", title: "", description: "", dueDate: "", maxMarks: "", allowLateSubmissions: false };

/**
 * The form's values for a loaded assignment. A date-only due date is shown as
 * the end of its day — the moment it actually closes — rather than as the
 * 05:30 its stored midnight-UTC would read as.
 */
const valuesFrom = (a, deadline) => ({
  batchId: idOf(a.batchId),
  title: a.title || "",
  description: a.description || "",
  dueDate: toDateTimeLocal(deadline || a.dueDate),
  maxMarks: a.maxMarks == null ? "" : String(a.maxMarks),
  allowLateSubmissions: a.allowLateSubmissions === true,
});

const QUICK_DUE = [
  { days: 1, label: "Tomorrow" },
  { days: 3, label: "In 3 days" },
  { days: 7, label: "In a week" },
];

/**
 * AssignmentFormPage — create or edit a batch assignment.
 * `/batch-assignments/create` (`?batchId=` preselects) · `/batch-assignments/edit/:id`,
 * and the same under `/i/`.
 *
 * The checks are the server's (`validateAssignmentForm` mirrors
 * vc_be/utils/generalAssignmentRules), shown on the field before anything is
 * sent. A file the server refuses comes back by name, and the form keeps
 * everything that was typed.
 */
const AssignmentFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const base = assignmentsBase(getCurrentUser()?.usertype);
  const upload = useUploadRequest();
  const formRef = useRef(null);

  const presetBatch = searchParams.get("batchId") || "";
  const [values, setValues] = useState({ ...BLANK, batchId: presetBatch });
  const [pristine, setPristine] = useState({ ...BLANK, batchId: presetBatch });
  const [original, setOriginal] = useState(null);
  const [kept, setKept] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [files, setFiles] = useState([]);
  const [batches, setBatches] = useState({ loading: !isEdit, list: [], total: 0 });
  const [loadState, setLoadState] = useState(isEdit ? "loading" : "ready");
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState("");

  useEffect(() => {
    if (isEdit) return;
    let alive = true;
    apiClient.get("/batches/list", { params: { pageSize: BATCH_PAGE } })
      .then(({ data }) => {
        if (alive) setBatches({ loading: false, list: data.batches || [], total: data.total || 0 });
      })
      .catch(() => alive && setBatches({ loading: false, list: [], total: 0, failed: true }));
    return () => { alive = false; };
  }, [isEdit]);

  const load = useCallback(() => {
    if (!isEdit) return;
    setLoadState("loading");
    apiClient.get(`/general-assignments/detail/${id}`)
      .then(({ data }) => {
        const loaded = valuesFrom(data.assignment, data.deadline);
        setOriginal(data.assignment);
        setValues(loaded);
        setPristine(loaded);
        setKept(data.assignment.attachments || []);
        setRemoved([]);
        setFiles([]);
        setLoadState("ready");
      })
      .catch((err) => setLoadState(err?.response?.status === 404 ? "missing" : "error"));
  }, [id, isEdit]);

  useEffect(() => { load(); }, [load]);

  const set = (key, value) => {
    setValues((v) => ({ ...v, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: "" }));
  };

  const reset = () => {
    setValues(pristine);
    setKept(original?.attachments || []);
    setRemoved([]);
    setFiles([]);
    setErrors({});
    setFailure("");
  };

  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate(isEdit ? `${base}/${id}` : base));

  const batchOptions = useMemo(() => {
    const list = batches.list.map((b) => ({ value: b._id, label: b.status === "Completed" ? `${b.name} (completed)` : b.name }));
    if (presetBatch && !list.some((o) => o.value === presetBatch)) list.unshift({ value: presetBatch, label: "This batch" });
    return list;
  }, [batches.list, presetBatch]);

  const fileSlots = MAX_FILES - kept.length;

  const submit = async (e) => {
    e.preventDefault();
    const next = validateAssignmentForm(values, { creating: !isEdit, original });
    if (files.length > fileSlots) next.attachments = `An assignment can carry up to ${MAX_FILES} files. Remove one first.`;
    setErrors(next);
    setFailure("");
    if (Object.values(next).some(Boolean)) {
      setTimeout(() => formRef.current?.querySelector('[aria-invalid="true"]')?.focus(), 0);
      return;
    }

    const fd = new FormData();
    fd.append("title", values.title.trim());
    fd.append("description", values.description.trim());
    if (!isEdit) fd.append("batchId", values.batchId);
    // An untouched due date is not sent: re-sending a date-only one as the
    // end-of-day it is shown as would quietly rewrite it.
    if (!isEdit || values.dueDate !== pristine.dueDate) fd.append("dueDate", fromDateTimeLocal(values.dueDate));
    fd.append("maxMarks", String(parseNumber(values.maxMarks)));
    fd.append("allowLateSubmissions", values.allowLateSubmissions ? "true" : "false");
    if (removed.length) fd.append("removeAttachments", JSON.stringify(removed));
    files.forEach((f) => fd.append("attachments", f));

    try {
      const data = await upload.send(
        isEdit ? "put" : "post",
        isEdit ? `/general-assignments/${id}` : "/general-assignments/add",
        fd,
      );
      navigate(`${base}/${data?.assignment?._id || id}`, {
        replace: true,
        state: { notice: isEdit ? "Assignment saved." : "Assignment created. Students in the batch can see it now." },
      });
    } catch (err) {
      const message = uploadFailure(err, "The assignment could not be saved. Try again.");
      if (message === null) return;
      const fieldErrors = err?.response?.data?.errors;
      if (fieldErrors) setErrors(fieldErrors);
      setFailure(message);
    }
  };

  const header = (
    <PageHeader
      icon={ClipboardList}
      title={isEdit ? "Edit assignment" : "New assignment"}
      description={isEdit ? original?.title || "" : "Text or file work for one batch, with a due date and marks"}
      backButton
      onBack={goBack}
    />
  );

  if (loadState === "loading") {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4" aria-busy="true" aria-label="Loading assignment">
        {header}
        <div className="tw-grid tw-gap-4 min-[992px]:tw-grid-cols-3">
          <div className="min-[992px]:tw-col-span-2"><Shimmer height={420} /></div>
          <Shimmer height={280} />
        </div>
      </div>
    );
  }
  if (loadState === "missing" || loadState === "error") {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {header}
        {loadState === "missing" ? (
          <EmptyState
            icon={SearchX}
            title="Assignment not found"
            description="It may have been deleted, or it belongs to a batch you don't teach."
            action={<AppButton variant="outline" onClick={() => navigate(base)}>Back to assignments</AppButton>}
          />
        ) : (
          <Callout tone="danger" role="alert" action={<AppButton size="xs" variant="outline" onClick={load}>Try again</AppButton>}>
            Could not load this assignment.
          </Callout>
        )}
      </div>
    );
  }

  const busy = upload.sending;
  const dueHint = values.dueDate && !Number.isNaN(new Date(values.dueDate).getTime())
    ? `${formatDue(values.dueDate)} — ${relativeTime(values.dueDate)}`
    : "Students see it until then.";

  return (
    <form ref={formRef} onSubmit={submit} noValidate className="has-form-actionbar tw-flex tw-flex-col tw-gap-4">
      {header}

      {failure && <Callout tone="danger" role="alert">{failure}</Callout>}

      <div className="tw-grid tw-items-start tw-gap-4 min-[992px]:tw-grid-cols-3">
        <AppCard className="min-[992px]:tw-col-span-2">
          <AppCardHeader>
            <AppCardTitle>What students do</AppCardTitle>
          </AppCardHeader>
          <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
            {isEdit ? (
              <FormField label="Batch" hint="An assignment stays with its batch — its students may already have handed work in.">
                <Input value={original?.batchId?.name || "—"} readOnly disabled size="lg" />
              </FormField>
            ) : (
              <FormField
                label="Batch"
                required
                error={errors.batchId}
                hint={batches.total > BATCH_PAGE
                  ? `Showing your ${BATCH_PAGE} most recent batches.`
                  : "Every student in this batch gets the assignment."}
              >
                <NativeSelect
                  size="lg"
                  value={values.batchId}
                  onChange={(e) => set("batchId", e.target.value)}
                  disabled={busy || batches.loading || Boolean(presetBatch)}
                >
                  <option value="">{batches.loading ? "Loading batches…" : "Choose a batch…"}</option>
                  {batchOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </NativeSelect>
              </FormField>
            )}
            {batches.failed && (
              <Callout tone="warning">The batch list could not be loaded. Reload the page to try again.</Callout>
            )}

            <FormField label="Title" required error={errors.title}>
              <Input
                size="lg"
                value={values.title}
                onChange={(e) => set("title", e.target.value)}
                placeholder="e.g. Build a to-do app with React"
                maxLength={LIMITS.title}
                disabled={busy}
              />
            </FormField>

            <FormField
              label="Instructions"
              error={errors.description}
              hint={`What to do and what to hand in. Links you paste become clickable for students. ${values.description.length.toLocaleString("en-IN")} / ${LIMITS.description.toLocaleString("en-IN")}`}
            >
              <Textarea
                rows={8}
                value={values.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder={"Describe the task, the format you expect, and how it will be marked.\nPaste a GitHub or Drive link if it helps."}
                maxLength={LIMITS.description}
                disabled={busy}
              />
            </FormField>

            <div className="tw-flex tw-flex-col tw-gap-2">
              {kept.length > 0 && (
                <FilePreview
                  attachments={kept}
                  onRemove={busy ? undefined : (key) => {
                    setKept((list) => list.filter((a) => a.fileKey !== key));
                    setRemoved((list) => [...list, key]);
                  }}
                />
              )}
              {fileSlots > 0 ? (
                <FileUpload
                  label={kept.length ? "Add reference files" : "Reference files (optional)"}
                  files={files}
                  onChange={(next) => {
                    setFiles(next);
                    if (errors.attachments) setErrors((e) => ({ ...e, attachments: "" }));
                  }}
                  accept={ACCEPT}
                  maxFiles={fileSlots}
                  maxSizeMB={MAX_FILE_MB}
                  validate={fileProblem}
                  hint="A brief, a dataset, a template — drop files here or browse"
                  typesHint={ACCEPTED_HINT}
                  disabled={busy}
                />
              ) : (
                <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">
                  {MAX_FILES} files is the most an assignment carries. Remove one to add another.
                </p>
              )}
              {errors.attachments && <Callout tone="danger">{errors.attachments}</Callout>}
            </div>
          </AppCardContent>
        </AppCard>

        <AppCard>
          <AppCardHeader>
            <AppCardTitle>Due date and marks</AppCardTitle>
            <AppCardDescription>Late work is refused unless you allow it.</AppCardDescription>
          </AppCardHeader>
          <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
            <FormField label="Due" required error={errors.dueDate} hint={dueHint}>
              <Input
                type="datetime-local"
                size="lg"
                value={values.dueDate}
                onChange={(e) => set("dueDate", e.target.value)}
                disabled={busy}
              />
            </FormField>
            <div className="tw-flex tw-flex-wrap tw-gap-1.5" role="group" aria-label="Quick due dates, 11:59 pm">
              {QUICK_DUE.map((q) => (
                <AppButton key={q.days} type="button" size="xs" variant="outline" disabled={busy} onClick={() => set("dueDate", dueInDays(q.days))}>
                  {q.label}
                </AppButton>
              ))}
            </div>

            <FormField label="Out of" required error={errors.maxMarks} hint="The most marks a student can get.">
              <Input
                type="number"
                inputMode="decimal"
                size="lg"
                min={1}
                max={LIMITS.maxMarks}
                step="any"
                value={values.maxMarks}
                onChange={(e) => set("maxMarks", e.target.value)}
                placeholder="e.g. 20"
                trailing={<span className="tw-text-[0.8125rem] tw-text-content-subtle">marks</span>}
                trailingPad="text"
                disabled={busy}
              />
            </FormField>

            <Checkbox
              variant="card"
              checked={values.allowLateSubmissions}
              onChange={(e) => set("allowLateSubmissions", e.target.checked)}
              disabled={busy}
              label="Accept late submissions"
              description="Students can still hand in after the due date. Their work is marked Late."
            />
          </AppCardContent>
        </AppCard>
      </div>

      <FormActionBar
        info={busy ? (
          <div className="tw-flex tw-w-48 tw-flex-col tw-gap-1" role="status" aria-live="polite">
            <span>{files.length ? `Uploading… ${upload.progress}%` : "Saving…"}</span>
            {files.length > 0 && <ProgressBar value={upload.progress} color="primary" />}
          </div>
        ) : null}
      >
        {busy ? (
          <AppButton type="button" variant="outline" onClick={upload.cancel}>Cancel upload</AppButton>
        ) : (
          <>
            <AppButton type="button" variant="outline" onClick={goBack}>Cancel</AppButton>
            <ResetButton onReset={reset} />
          </>
        )}
        <AppButton type="submit" disabled={busy}>
          {busy ? "Saving…" : isEdit ? "Save changes" : "Create assignment"}
        </AppButton>
      </FormActionBar>
    </form>
  );
};

export default AssignmentFormPage;
